/**
 * Zmenšená osa nad kalendářem (jen režim „Osa“): načtené okno plus rezerva na obou stranách,
 * a v nich rámeček aktuálního výřezu. Slouží k hrubému skoku — kliknutí i tažení posune výřez
 * na dané místo. Skok do rezervy za okno je legitimní: kalendář si ten měsíc donačte.
 *
 * Měsíce mají **minimální šířku** ({@link MIN_MONTH_PX}); okno může být dlouhé tři roky a nacpat
 * ho celé do pruhu by z měsíců udělalo nečitelné proužky. Osa je proto klidně širší než pruh —
 * pak je **uříznutá** a posouvá se pod ním. Ne ale pořád: teprve když se rámeček výřezu přiblíží
 * ke kraji, osa poskočí tak, aby ho zase měla s rezervou uvnitř (viz {@link panMiniAxis}).
 */
import { dayNumberOf, monthIndex, shiftMonth } from './days';
import { windowRange, type CalendarWindow } from './calendarWindow';

/** Nejužší měsíc na ose. Kratší se nedá přečíst ani pohodlně trefit myší. */
export const MIN_MONTH_PX = 70;
/** Nejkratší měsíc v roce — z něj plyne šířka dne, aby i únor vyšel na {@link MIN_MONTH_PX}. */
const SHORTEST_MONTH_DAYS = 28;
/** Jak blízko ke kraji pruhu smí rámeček výřezu dojet, než se osa posune. */
const EDGE_MARGIN_PX = 80;
/** Rezerva měsíců, kterou osa ukazuje za hranami načteného okna. */
const PADDING_MONTHS = 3;
/** Nejmenší vykreslený rámeček výřezu — pár dnů by jinak zmizelo pod vlasovou linkou. */
const MIN_VIEWPORT_PX = 9;

export interface MiniAxisMonth {
  key: string;
  /** Šířka v px. */
  width: number;
  label: string;
  /** Měsíc spadá do vykresleného okna kalendáře (zvýrazněné pozadí). */
  inWindow: boolean;
}

export interface MiniAxisModel {
  /** První den zmenšené osy. */
  start: number;
  /** Kolik dnů osa pokrývá. */
  days: number;
  /** Šířka dne na ose v px. */
  pxPerDay: number;
  /** Celková šířka osy v px; větší než pruh znamená, že je osa uříznutá. */
  contentWidth: number;
  months: MiniAxisMonth[];
  /** Rámeček výřezu v souřadnicích osy (px od jejího začátku). */
  viewportLeft: number;
  viewportWidth: number;
}

export interface MiniAxisInput {
  /** Načtené okno měsíců. */
  window: CalendarWindow;
  monthNames: readonly string[];
  /** Vodorovný posuv scrolleru v px. */
  scrollLeft: number;
  /** Viditelná šířka scrolleru v px. */
  viewportPx: number;
  /** Šířka dne v px na ose. */
  dayWidth: number;
  /** Šířka pruhu, do kterého se osa kreslí. */
  trackPx: number;
}

export function buildMiniAxis(input: MiniAxisInput): MiniAxisModel {
  const { monthNames: names } = input;
  const from = shiftMonth(input.window.first, -PADDING_MONTHS);
  const to = shiftMonth(input.window.last, PADDING_MONTHS);
  const count = monthIndex(to) - monthIndex(from) + 1;

  const start = dayNumberOf(from.year, from.month, 1);
  const end = dayNumberOf(to.year, to.month + 1, 0);
  const days = end - start + 1;
  const range = windowRange(input.window);

  // Den je tak široký, aby se osa buď vešla do pruhu, nebo aby i nejkratší měsíc vyšel na minimum.
  const pxPerDay = Math.max(
    MIN_MONTH_PX / SHORTEST_MONTH_DAYS,
    days > 0 ? input.trackPx / days : 0
  );

  const months: MiniAxisMonth[] = [];
  for (let index = 0; index < count; index += 1) {
    const first = dayNumberOf(from.year, from.month + index, 1);
    const last = dayNumberOf(from.year, from.month + index + 1, 0);
    const date = new Date(first * 86_400_000);
    const month = date.getUTCMonth();
    months.push({
      key: String(first),
      width: (last - first + 1) * pxPerDay,
      label:
        (names[month] ?? '').slice(0, 3) +
        (month === 0 ? ` ${String(date.getUTCFullYear()).slice(2)}` : ''),
      inWindow: first <= range.end && last >= range.start,
    });
  }

  const dayWidth = input.dayWidth || 1;
  const visibleFrom = range.start + input.scrollLeft / dayWidth;
  const visibleDays = input.viewportPx / dayWidth;

  return {
    start,
    days,
    pxPerDay,
    contentWidth: days * pxPerDay,
    months,
    viewportLeft: (visibleFrom - start) * pxPerDay,
    viewportWidth: visibleDays * pxPerDay,
  };
}

/**
 * Posun osy pod pruhem. Drží se na místě, dokud je rámeček výřezu pohodlně vidět; teprve když se
 * přiblíží ke kraji, poskočí právě tak, aby ho zase měl s rezervou uvnitř. Bez té setrvačnosti by
 * se osa hýbala při každém pohnutí kalendářem a nedalo by se z ní nic přečíst.
 */
export function panMiniAxis(model: MiniAxisModel, trackPx: number, current: number): number {
  const max = Math.max(0, model.contentWidth - trackPx);
  if (max === 0) return 0;

  const left = model.viewportLeft;
  const right = model.viewportLeft + Math.max(model.viewportWidth, MIN_VIEWPORT_PX);

  let offset = Math.min(Math.max(current, 0), max);
  if (left < offset + EDGE_MARGIN_PX) {
    offset = left - EDGE_MARGIN_PX;
  } else if (right > offset + trackPx - EDGE_MARGIN_PX) {
    offset = right - trackPx + EDGE_MARGIN_PX;
  }
  return Math.min(Math.max(offset, 0), max);
}

/** Den, na který na ose ukazuje daná pozice v pruhu (px od jeho levého kraje). */
export function dayAtMiniAxisX(model: MiniAxisModel, offset: number, x: number): number {
  return model.start + (x + offset) / model.pxPerDay;
}
